import { FC, FormEvent, RefObject, useEffect, useRef, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcModal from "shared/NcModal/NcModal";
import { AddCustomer } from "api/customer/customer";

/**
 * 1NOW-10770: a returning renter's email at checkout is proved with a code mailed to it before any booking is made.
 *
 * It is add-website-customer again, not a new endpoint. With `email_code: true` a renter who has booked with this
 * company before gets `{ code_required: true, detail, resend_after }` and a six-digit code in their inbox instead of
 * a session; the same body plus `code` answers with the full session. A new renter, and an email with no booking
 * here, get the session straight back, so they never see this step.
 */

export interface CodeCustomer {
  first_name: string;
  last_name: string;
  email: string;
  phone_no: string;
  company: number | string;
}

/** What the car page gets back: the session (as AddCustomer gives it), an error to toast, or "nothing to do". */
export interface CustomerOutcome {
  data?: any;
  error?: string;
  /** The renter closed the code step (or chose another email): no booking is made. */
  cancelled?: boolean;
}

const CODE_LENGTH = 6;
const RESEND_AFTER_SECONDS = 60;
const BAD_CODE = "That code isn't right or has expired.";

export const codeRequired = (data: any): boolean => data?.code_required === true;
export const cleanCode = (value: string) => String(value ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);
/** s***@gmail.com */
export const maskEmail = (email: string) => {
  const [local, domain] = email.trim().split("@");
  return local && domain ? `${local.charAt(0)}***@${domain}` : "";
};
/** 125 -> "2:05": the wait between codes runs to minutes. */
const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

interface Gate {
  customer: CodeCustomer;
  mode: "now" | "later";
  /** Seconds until another code may be asked for, as the backend said. */
  resendAfter?: number;
  /** Why no code works yet (asked again too soon), shown from the start. */
  error?: string;
  onVerify: (code: string) => Promise<{ error?: string }>;
  onClose: () => void;
  onDifferentEmail: () => void;
}

const linkCls =
  "text-sm font-semibold underline disabled:no-underline disabled:opacity-60";

/** The six-digit field: one real input lies over six boxes, so typing, paste and one-time-code autofill all work. */
const CodeBoxes: FC<{
  value: string;
  onChange: (next: string) => void;
  invalid: boolean;
  inputRef: RefObject<HTMLInputElement>;
}> = ({ value, onChange, invalid, inputRef }) => {
  const [focused, setFocused] = useState(false);
  return (
    <>
      <label className="block text-sm font-semibold mb-1.5" htmlFor="email-code">
        6-digit code
      </label>
      <div className="relative w-full max-w-[22rem]">
        <div className="grid grid-cols-6 gap-1.5 sm:gap-2" aria-hidden="true" data-testid="code-boxes">
          {Array.from({ length: CODE_LENGTH }, (_, i) => {
            const active = focused && i === Math.min(value.length, CODE_LENGTH - 1);
            return (
              <span
                key={i}
                className={`flex h-12 items-center justify-center rounded-md border bg-white text-xl font-semibold dark:bg-transparent ${
                  invalid
                    ? "border-red-500"
                    : active
                    ? "border-primary-300 ring ring-primary-200 ring-opacity-50"
                    : "border-neutral-200 dark:border-white"
                }`}
              >
                {value[i] ?? ""}
              </span>
            );
          })}
        </div>
        <input
          id="email-code"
          name="code"
          ref={inputRef}
          data-autofocus
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          value={value}
          onChange={(e) => onChange(cleanCode(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={invalid}
          className="absolute inset-0 h-full w-full cursor-text border-0 bg-transparent text-transparent caret-transparent opacity-0 focus:ring-0"
        />
      </div>
    </>
  );
};

/**
 * "Check your email": six digits are checked at once (or with Continue); a refused code shows the backend's own
 * words with red boxes; "Resend code" works after the wait. The host says what a right code does (`onVerify`).
 */
const EmailCodeBlock: FC<{ gate: Gate }> = ({ gate }) => {
  const { customer, mode, onVerify, onDifferentEmail } = gate;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(gate.error ?? "");
  const [bad, setBad] = useState(false);
  const [notice, setNotice] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(gate.resendAfter ?? 0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // The resend wait counts down while the box is open.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const verify = async (value: string) => {
    if (busy) return;
    if (value.length !== CODE_LENGTH) {
      setProblem(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setProblem("");
    setNotice("");
    setBad(false);
    setBusy(true);
    const result = await onVerify(value);
    setBusy(false);
    if (result.error) {
      setProblem(result.error);
      setBad(true);
    }
  };

  // "Resend code" is the same call without `code`: a new mail once the wait is over, "already sent" while the last
  // code still works, the wait in words when it is too soon.
  const resend = async () => {
    if (busy || secondsLeft > 0) return;
    setProblem("");
    setBad(false);
    setNotice("");
    setBusy(true);
    const r = await AddCustomer({ ...customer, email_code: true });
    setBusy(false);
    const wait = Number(r.data?.resend_after) || undefined;
    if (r.error || r.data?.error) {
      setProblem(r.error ?? r.data.error);
      if (wait) setSecondsLeft(wait);
    } else {
      setCode("");
      setSecondsLeft(wait ?? RESEND_AFTER_SECONDS);
      setNotice(r.data?.detail ?? "We sent you a new code.");
    }
  };

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void verify(code);
      }}
      noValidate
      className="ph-no-capture text-neutral-900 dark:text-neutral-200"
      data-testid="email-code"
    >
      <p className="mb-4 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
        You&apos;ve booked with us before. Enter the code we sent to{" "}
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{maskEmail(customer.email)}</span>. It
        works for 10 minutes.
      </p>
      {problem && (
        <div role="alert" className="mb-4 rounded-md bg-red-50 p-3 text-sm font-semibold text-red-800">
          {problem}
        </div>
      )}
      {notice && !problem && (
        <p role="status" className="mb-4 text-sm font-semibold">
          {notice}
        </p>
      )}
      <CodeBoxes
        value={code}
        inputRef={codeRef}
        invalid={bad}
        onChange={(next) => {
          setCode(next);
          if (bad) setBad(false);
          if (next.length === CODE_LENGTH) void verify(next);
        }}
      />
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <ButtonPrimary type="submit" disabled={busy} className="min-w-[160px]">
          {busy ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            "Continue"
          )}
        </ButtonPrimary>
        <button type="button" onClick={resend} disabled={busy || secondsLeft > 0} className={linkCls}>
          {secondsLeft > 0 ? `Resend code in ${clock(secondsLeft)}` : "Resend code"}
        </button>
      </div>
      <button type="button" onClick={onDifferentEmail} className={`mt-4 block ${linkCls}`}>
        Use a different email
      </button>
      <p className="mt-4 text-xs text-neutral-500 dark:text-neutral-400">
        {mode === "now"
          ? "Payment opens once your code is confirmed."
          : "Your car is not reserved yet. We reserve it as soon as the code is confirmed."}
      </p>
    </form>
  );
};

/**
 * The car page's way to the renter's session. `addCustomer` is AddCustomer with the code step in the middle: a new
 * renter's session comes straight back; a returning renter's email opens "Check your email" (render `emailCodeModal`
 * once on the page) and the call waits for the right code, so the page's booking code after it runs unchanged and
 * only with the returned session. `{ cancelled: true }` means nothing was booked: the page just stops.
 *
 * A code can be spent once, so the session a right code returned is kept for this email: if the booking that
 * follows fails, trying again carries on with it instead of asking for a new code.
 * "Use a different email" closes the step and calls `onDifferentEmail` (the page clears and focuses its email field).
 */
export const useEmailCode = (onDifferentEmail: () => void) => {
  const [gate, setGate] = useState<Gate | null>(null);
  const verified = useRef<{ email: string; data: any } | null>(null);

  const addCustomer = async (customer: CodeCustomer, mode: "now" | "later"): Promise<CustomerOutcome> => {
    const email = customer.email.trim().toLowerCase();
    if (verified.current?.email === email) return { data: verified.current.data };

    const first = await AddCustomer({ ...customer, email_code: true });
    if (!codeRequired(first.data)) return first;

    return new Promise<CustomerOutcome>((resolve) => {
      const close = (outcome: CustomerOutcome) => {
        setGate(null);
        resolve(outcome);
      };
      setGate({
        customer,
        mode,
        resendAfter: Number(first.data.resend_after) || undefined,
        error: first.data.error,
        onVerify: async (code) => {
          const r = await AddCustomer({ ...customer, email_code: true, code });
          if (r.error) return { error: r.error };
          // A right code is answered with the session, never with another request for a code.
          if (codeRequired(r.data)) return { error: r.data.error ?? BAD_CODE };
          verified.current = { email, data: r.data };
          close({ data: r.data });
          return {};
        },
        onClose: () => close({ cancelled: true }),
        onDifferentEmail: () => {
          close({ cancelled: true });
          onDifferentEmail();
        },
      });
    });
  };

  const emailCodeModal = gate && (
    <NcModal
      isOpenProp
      onCloseModal={gate.onClose}
      modalTitle="Check your email"
      contentExtraClass="max-w-md"
      renderContent={() => <EmailCodeBlock gate={gate} />}
    />
  );

  return { addCustomer, emailCodeModal };
};
